<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappContactInsightsAPI from 'dashboard/api/whatsappContactInsights';
import LaisWhatsappConnectionsAPI from 'dashboard/api/laisWhatsappConnections';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: [Number, String],
    default: null,
  },
});

const { t, tm, locale } = useI18n();
const insight = ref(null);
const connections = ref([]);
const isLoading = ref(false);
const isRefreshingProfile = ref(false);
const isSubscribing = ref(false);
const hasLoadError = ref(false);

const responseData = response =>
  response?.data?.data || response?.data || response;
const asObject = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const toArray = value => {
  if (Array.isArray(value)) return value;
  return value === undefined || value === null ? [] : [value];
};
const asEntries = value => Object.entries(asObject(value));
const translationKey = value =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const connection = computed(() =>
  connections.value.find(
    item => Number(item.inbox_id) === Number(props.inboxId)
  )
);
const profileSection = computed(() => asObject(insight.value?.profile));
const activeRefresh = computed(() =>
  asObject(profileSection.value.active_refresh)
);
const activeProbes = computed(() => asObject(activeRefresh.value.probes));
const activeProfile = computed(() =>
  asObject(asObject(activeProbes.value.profile).data)
);
const activeBusinessProfile = computed(() =>
  asObject(asObject(activeProbes.value.business_profile).data)
);
const profile = computed(() => ({
  ...asObject(profileSection.value.current),
  ...activeBusinessProfile.value,
  ...activeProfile.value,
}));
const presence = computed(() => asObject(insight.value?.presence));
const messaging = computed(() => asObject(insight.value?.messaging));
const responseTime = computed(() => asObject(messaging.value.response_time));
const receiptAnalytics = computed(() =>
  asObject(messaging.value.receipt_analytics)
);
const lifecycle = computed(() => asObject(messaging.value.message_lifecycle));
const chat = computed(() => asObject(insight.value?.chat));
const groups = computed(() => asObject(insight.value?.groups));
const links = computed(() => asObject(insight.value?.link_previews));
const calls = computed(() => asObject(insight.value?.calls));
const limitations = computed(() => asObject(insight.value?.limitations));
const deliveryLatency = computed(() =>
  asObject(receiptAnalytics.value.delivery_latency)
);
const readLatency = computed(() =>
  asObject(receiptAnalytics.value.read_latency)
);

const currentPresence = computed(() => asObject(presence.value.current));
const openPresenceSessions = computed(() =>
  toArray(presence.value.open_sessions).filter(Boolean)
);
const completedPresenceSessions = computed(() =>
  toArray(presence.value.completed_sessions).filter(Boolean)
);
const latestCompletedPresenceSession = computed(
  () => completedPresenceSessions.value[0] || {}
);
const isPresenceOnline = computed(() => {
  const unavailable = currentPresence.value.unavailable;
  return (
    currentPresence.value.currently_online === true ||
    currentPresence.value.state === 'online' ||
    unavailable === false ||
    unavailable === 'false' ||
    openPresenceSessions.value.some(session => session.currently_online)
  );
});
const onlineHours = computed(() => {
  const source =
    presence.value.most_observed_online_hours || presence.value.hourly || {};
  const items = Array.isArray(source)
    ? source
    : asEntries(source).map(([hour, onlineSeconds]) => ({
        hour: Number(hour),
        online_seconds: onlineSeconds,
      }));

  const normalized = items
    .map(item => ({
      hour: Number(item.hour ?? item.local_hour ?? 0),
      onlineSeconds: Number(
        item.online_seconds ??
          item.observed_seconds ??
          item.seconds ??
          item.value ??
          0
      ),
    }))
    .filter(item => Number.isFinite(item.hour))
    .sort((left, right) => right.onlineSeconds - left.onlineSeconds);
  const maximum = Math.max(...normalized.map(item => item.onlineSeconds), 1);

  return normalized.map(item => ({
    ...item,
    segments: Math.max(1, Math.round((item.onlineSeconds / maximum) * 12)),
  }));
});

const messageKinds = computed(() =>
  asEntries(messaging.value.message_kind_count)
);
const providerMessageTypes = computed(() =>
  asEntries(messaging.value.provider_message_type_count)
);
const mediaKinds = computed(() => asEntries(messaging.value.media_kind_count));
const interactionTypes = computed(() =>
  asEntries(messaging.value.interaction_type_count)
);
const messageDetailMetrics = computed(() => [
  ...mediaKinds.value.map(([label, count]) => [`media:${label}`, label, count]),
  ...interactionTypes.value.map(([label, count]) => [
    `interaction:${label}`,
    label,
    count,
  ]),
]);
const features = computed(() =>
  asEntries(messaging.value.feature_count).filter(
    ([, count]) => Number(count) > 0
  )
);
const deliveryStates = computed(() =>
  asEntries(
    receiptAnalytics.value.type_count ||
      messaging.value.delivery_status_count ||
      messaging.value.delivery_statuses
  )
);
const lifecycleItems = computed(() =>
  asEntries(lifecycle.value).filter(([, count]) => Number(count) > 0)
);
const profileAliases = computed(() =>
  [
    ...toArray(profileSection.value.aliases),
    ...toArray(profile.value.jids),
    ...toArray(activeRefresh.value.queried_jids),
  ]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
);
const profileInfoItems = computed(() =>
  [
    ['push_name', profile.value.push_name],
    ['business_name', profile.value.business_name],
    ['name', profile.value.name || profile.value.display_name],
    ['about', profile.value.about || profile.value.status],
    ['category', profile.value.category || profile.value.business_category],
    ['email', profile.value.email],
    ['website', profile.value.website],
    ['address', profile.value.address],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '')
);
const chatLabels = computed(() =>
  toArray(chat.value.labels).filter(label => !asObject(label).deleted)
);
const chatStateItems = computed(() =>
  [
    ['archived', chat.value.current?.archived],
    ['read_only', chat.value.current?.read_only],
    ['not_spam', chat.value.current?.not_spam],
    ['unread_count', chat.value.current?.unread_count],
    ['unread_mention_count', chat.value.current?.unread_mention_count],
    ['participant_count', chat.value.current?.participant_count],
    ['ephemeral_expiration', chat.value.current?.ephemeral_expiration],
    ['observed_at', chat.value.current?.observed_at],
  ].filter(([, value]) => value !== undefined && value !== null && value !== '')
);
const groupMemberships = computed(() =>
  toArray(groups.value.current).filter(Boolean)
);
const linkItems = computed(() => toArray(links.value.items).filter(Boolean));
const callItems = computed(() =>
  [
    ...toArray(calls.value.recent),
    ...toArray(calls.value.observed_history),
  ].filter(Boolean)
);
const profileEvents = computed(() =>
  toArray(insight.value?.profile_events).filter(Boolean)
);
const limitationItems = computed(() => asEntries(limitations.value));

const profileName = computed(
  () =>
    profile.value.push_name ||
    profile.value.business_name ||
    profile.value.name ||
    t('WHATSAPP_INTELLIGENCE.UNKNOWN')
);
const onlineDays = computed(() =>
  Array.isArray(presence.value.daily)
    ? [...presence.value.daily].reverse()
    : Object.values(asObject(presence.value.daily)).reverse()
);

const currentLocale = computed(() =>
  String(locale.value || 'en').replace('_', '-')
);

const parsePresenceTime = value => {
  if (!value) return null;

  if (typeof value === 'number') {
    if (value <= 0) return null;

    const timestamp = value > 1000000000000 ? value : value * 1000;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    const numericValue = Number(value);
    if (numericValue <= 0) return null;

    const timestamp =
      numericValue > 1000000000000 ? numericValue : numericValue * 1000;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = value => {
  const date = parsePresenceTime(value);
  if (!date) return t('WHATSAPP_INTELLIGENCE.UNKNOWN');
  return new Intl.DateTimeFormat(currentLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatDay = value => {
  const date = parsePresenceTime(value);
  if (!date) return t('WHATSAPP_INTELLIGENCE.UNKNOWN');

  return new Intl.DateTimeFormat(currentLocale.value, {
    dateStyle: 'medium',
  }).format(date);
};

const hasExplicitLastSeen = computed(() =>
  Boolean(currentPresence.value.last_seen_at || currentPresence.value.last_seen)
);

const metricLabels = computed(() =>
  asObject(tm('WHATSAPP_INTELLIGENCE.METRICS'))
);
const callStatusLabels = computed(() =>
  asObject(tm('WHATSAPP_INTELLIGENCE.CALL_STATUSES'))
);

const lastSeenValue = computed(
  () =>
    currentPresence.value.last_seen_at ||
    currentPresence.value.last_seen ||
    latestCompletedPresenceSession.value.last_seen_at ||
    latestCompletedPresenceSession.value.ended_at ||
    currentPresence.value.last_offline_at ||
    presence.value.last_seen_at ||
    currentPresence.value.updated_at ||
    null
);

const presenceLabel = computed(() => {
  if (isPresenceOnline.value) return t('WHATSAPP_PRESENCE.ONLINE');
  if (!lastSeenValue.value) return t('WHATSAPP_PRESENCE.UNAVAILABLE');

  if (hasExplicitLastSeen.value) {
    return t('WHATSAPP_PRESENCE.LAST_SEEN', {
      time: formatDate(lastSeenValue.value),
    });
  }

  return t('WHATSAPP_PRESENCE.LAST_ONLINE_OBSERVED', {
    time: formatDate(lastSeenValue.value),
  });
});

const formatDuration = value => {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds) || seconds <= 0)
    return t('WHATSAPP_INTELLIGENCE.NO_DATA');
  if (seconds < 60)
    return t('WHATSAPP_INTELLIGENCE.SECONDS', { count: Math.round(seconds) });
  if (seconds < 3600)
    return t('WHATSAPP_INTELLIGENCE.MINUTES', {
      count: Math.round(seconds / 60),
    });
  return t('WHATSAPP_INTELLIGENCE.HOURS', {
    count: (seconds / 3600).toFixed(1),
  });
};

const formatLabel = value =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
const translateMetricLabel = value =>
  metricLabels.value[translationKey(value)] || formatLabel(value);
const translateCallStatus = value =>
  callStatusLabels.value[translationKey(value)] || formatLabel(value);
const formatHour = hour =>
  t('WHATSAPP_INTELLIGENCE.HOUR', {
    hour: String(hour).padStart(2, '0'),
  });
const formatMetric = (label, count) =>
  t('WHATSAPP_INTELLIGENCE.METRIC_COUNT', {
    label: translateMetricLabel(label),
    count,
  });
const formatValue = value => {
  if (typeof value === 'boolean') {
    return value
      ? t('WHATSAPP_INTELLIGENCE.YES')
      : t('WHATSAPP_INTELLIGENCE.NO');
  }
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
const formatChatValue = (key, value) =>
  key.endsWith('_at') ? formatDate(value) : formatValue(value);
const labelName = label => {
  const item = asObject(label);
  return item.name || item.label || item.id || formatValue(label);
};
const groupName = membership =>
  membership.name ||
  membership.subject ||
  membership.topic ||
  membership.group_jid ||
  t('WHATSAPP_INTELLIGENCE.UNKNOWN');
const formatGroupMembership = membership =>
  [
    translateMetricLabel(membership.membership_state || 'member'),
    translateMetricLabel(membership.role),
    membership.participant_count === undefined
      ? null
      : t('WHATSAPP_INTELLIGENCE.MEMBER_COUNT', {
          count: membership.participant_count,
        }),
    membership.observed_at ? formatDate(membership.observed_at) : null,
  ]
    .filter(Boolean)
    .join(' · ');
const formatCall = call =>
  t('WHATSAPP_INTELLIGENCE.CALL_ENTRY', {
    status: translateCallStatus(
      call.status || call.call_result || call.event || call.direction
    ),
    time: formatDate(
      call.started_at || call.start_time || call.occurred_at || call.created_at
    ),
  });
const formatPresenceSession = session =>
  t('WHATSAPP_INTELLIGENCE.SESSION_ENTRY', {
    start: formatDate(session.started_at),
    end: formatDate(session.ended_at || session.last_seen_at),
    duration: formatDuration(session.observed_seconds),
  });

const load = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  hasLoadError.value = false;
  try {
    const [insightResponse, connectionResponse] = await Promise.all([
      WhatsappContactInsightsAPI.get(props.contactId),
      LaisWhatsappConnectionsAPI.list(),
    ]);
    insight.value = responseData(insightResponse);
    connections.value = toArray(responseData(connectionResponse));
  } catch (error) {
    hasLoadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const refreshProfile = async () => {
  if (!connection.value?.id) return;

  isRefreshingProfile.value = true;
  try {
    await WhatsappContactInsightsAPI.refreshProfile(
      props.contactId,
      connection.value.id
    );
    useAlert(t('WHATSAPP_INTELLIGENCE.PROFILE_REFRESHED'));
    await load();
  } catch (error) {
    useAlert(t('WHATSAPP_INTELLIGENCE.ACTION_FAILED'));
  } finally {
    isRefreshingProfile.value = false;
  }
};

const subscribePresence = async () => {
  isSubscribing.value = true;
  try {
    await WhatsappContactInsightsAPI.subscribePresence(props.contactId);
    useAlert(t('WHATSAPP_INTELLIGENCE.PRESENCE_SUBSCRIBED'));
    await load();
  } catch (error) {
    useAlert(t('WHATSAPP_INTELLIGENCE.ACTION_FAILED'));
  } finally {
    isSubscribing.value = false;
  }
};

watch(() => [props.contactId, props.inboxId], load, { immediate: true });
</script>

<template>
  <section
    class="rounded-xl border border-n-weak bg-n-background px-3 py-4 shadow-sm"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('WHATSAPP_INTELLIGENCE.SUBTITLE') }}
        </p>
      </div>
      <Button
        :aria-label="$t('WHATSAPP_INTELLIGENCE.REFRESH')"
        color="slate"
        icon="i-ph-arrow-clockwise"
        size="sm"
        variant="faded"
        @click="load"
      />
    </div>

    <div
      v-if="isLoading && !insight"
      class="py-7 text-center text-sm text-n-slate-11"
    >
      {{ $t('WHATSAPP_INTELLIGENCE.LOADING') }}
    </div>

    <div v-else-if="hasLoadError" class="py-5 text-center">
      <p class="text-sm text-n-slate-11">
        {{ $t('WHATSAPP_INTELLIGENCE.LOAD_FAILED') }}
      </p>
      <Button
        class="mt-3"
        :label="$t('WHATSAPP_INTELLIGENCE.RETRY')"
        color="slate"
        size="sm"
        variant="faded"
        @click="load"
      />
    </div>

    <div v-else-if="insight" class="mt-4 space-y-4">
      <div
        class="flex items-center gap-3 rounded-lg bg-n-slate-3 px-3 py-3 dark:bg-n-slate-4"
      >
        <div
          class="flex size-10 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
        >
          <i class="i-ph-whatsapp-logo text-xl" />
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-n-slate-12">
            {{ profileName }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ presenceLabel }}
          </p>
        </div>
        <span
          class="size-2 rounded-full"
          :class="isPresenceOnline ? 'bg-g-600' : 'bg-n-slate-8'"
        />
      </div>

      <div class="grid grid-cols-3 gap-2">
        <div class="rounded-lg border border-n-weak px-2 py-2">
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.RESPONSE') }}
          </p>
          <p class="mt-1 text-sm font-semibold text-n-slate-12">
            {{ formatDuration(responseTime.average_seconds) }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-2 py-2">
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.DELIVERED') }}
          </p>
          <p class="mt-1 text-sm font-semibold text-n-slate-12">
            {{ formatDuration(deliveryLatency.average_seconds) }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-2 py-2">
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.READ') }}
          </p>
          <p class="mt-1 text-sm font-semibold text-n-slate-12">
            {{ formatDuration(readLatency.average_seconds) }}
          </p>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.TOTAL_MESSAGES') }}
          </p>
          <p class="mt-1 text-sm font-semibold text-n-slate-12">
            {{ messaging.total || 0 }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak px-3 py-2">
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.MESSAGE_DIRECTIONS') }}
          </p>
          <p class="mt-1 text-xs font-medium text-n-slate-12">
            {{
              $t('WHATSAPP_INTELLIGENCE.INCOMING_OUTGOING', {
                incoming: messaging.incoming || 0,
                outgoing: messaging.outgoing || 0,
              })
            }}
          </p>
        </div>
      </div>

      <div
        v-if="profileInfoItems.length || profileAliases.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.PROFILE_DETAILS') }}
        </p>
        <dl v-if="profileInfoItems.length" class="mt-2 space-y-1.5">
          <div
            v-for="[key, value] in profileInfoItems"
            :key="key"
            class="flex items-start justify-between gap-3 text-xs"
          >
            <dt class="text-n-slate-11">{{ translateMetricLabel(key) }}</dt>
            <dd class="min-w-0 break-words text-right text-n-slate-12">
              {{ formatValue(value) }}
            </dd>
          </div>
        </dl>
        <div v-if="profileAliases.length" class="mt-3">
          <p class="text-[11px] font-medium text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.ALIASES') }}
          </p>
          <p
            v-for="alias in profileAliases"
            :key="alias"
            class="mt-1 break-all text-[11px] text-n-slate-12"
          >
            {{ alias }}
          </p>
        </div>
      </div>

      <div
        v-if="onlineHours.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_INTELLIGENCE.BEST_HOURS') }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_INTELLIGENCE.OBSERVED_ONLINE') }}
          </p>
        </div>
        <div class="mt-3 space-y-2">
          <div
            v-for="item in onlineHours"
            :key="item.hour"
            class="flex items-center gap-2"
          >
            <span class="w-11 text-xs text-n-slate-11">
              {{ formatHour(item.hour) }}
            </span>
            <div class="flex h-2 flex-1 gap-0.5 overflow-hidden rounded-full">
              <span
                v-for="segment in 12"
                :key="segment"
                class="h-full flex-1 rounded-full"
                :class="
                  segment <= item.segments
                    ? 'bg-n-brand'
                    : 'bg-n-slate-4 dark:bg-n-slate-5'
                "
              />
            </div>
            <span class="w-12 text-right text-[11px] text-n-slate-11">
              {{ formatDuration(item.onlineSeconds) }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="onlineDays.length" class="rounded-lg border border-n-weak p-3">
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.RECENT_PRESENCE') }}
        </p>
        <div class="mt-2 grid grid-cols-2 gap-2">
          <div
            v-for="day in onlineDays"
            :key="day.date"
            class="rounded-md bg-n-slate-3 px-2 py-1.5 dark:bg-n-slate-4"
          >
            <p class="truncate text-[11px] text-n-slate-11">
              {{ formatDay(day.date) }}
            </p>
            <p class="text-xs font-medium text-n-slate-12">
              {{ formatDuration(day.online_seconds) }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="completedPresenceSessions.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.SESSION_HISTORY') }}
        </p>
        <p
          v-for="session in completedPresenceSessions"
          :key="`${session.contact_jid}-${session.started_at}`"
          class="mt-2 text-xs text-n-slate-11"
        >
          {{ formatPresenceSession(session) }}
        </p>
      </div>

      <div
        v-if="
          messageKinds.length ||
          providerMessageTypes.length ||
          mediaKinds.length ||
          interactionTypes.length ||
          features.length
        "
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.MESSAGE_PROFILE') }}
        </p>
        <div v-if="messageKinds.length" class="mt-2 flex flex-wrap gap-1.5">
          <span
            v-for="[kind, count] in messageKinds"
            :key="kind"
            class="rounded-full bg-n-slate-3 px-2 py-1 text-[11px] text-n-slate-11 dark:bg-n-slate-4"
          >
            {{ formatMetric(kind, count) }}
          </span>
        </div>
        <div v-if="features.length" class="mt-2 flex flex-wrap gap-1.5">
          <span
            v-for="[feature, count] in features"
            :key="feature"
            class="rounded-full bg-n-brand/10 px-2 py-1 text-[11px] text-n-brand"
          >
            {{ formatMetric(feature, count) }}
          </span>
        </div>
        <div
          v-if="providerMessageTypes.length"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <span
            v-for="[kind, count] in providerMessageTypes"
            :key="kind"
            class="rounded-full bg-n-slate-3 px-2 py-1 text-[11px] text-n-slate-11 dark:bg-n-slate-4"
          >
            {{ formatMetric(kind, count) }}
          </span>
        </div>
        <div
          v-if="messageDetailMetrics.length"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <span
            v-for="[key, kind, count] in messageDetailMetrics"
            :key="key"
            class="rounded-full bg-n-brand/10 px-2 py-1 text-[11px] text-n-brand"
          >
            {{ formatMetric(kind, count) }}
          </span>
        </div>
      </div>

      <div
        v-if="deliveryStates.length || lifecycleItems.length"
        class="grid grid-cols-2 gap-2"
      >
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-xs font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_INTELLIGENCE.DELIVERY_STATUS') }}
          </p>
          <p
            v-for="[state, count] in deliveryStates"
            :key="state"
            class="mt-1 text-xs text-n-slate-11"
          >
            {{ formatMetric(state, count) }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <p class="text-xs font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_INTELLIGENCE.LIFECYCLE') }}
          </p>
          <p
            v-for="[state, count] in lifecycleItems"
            :key="state"
            class="mt-1 text-xs text-n-slate-11"
          >
            {{ formatMetric(state, count) }}
          </p>
        </div>
      </div>

      <div
        v-if="groupMemberships.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_INTELLIGENCE.GROUPS') }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{
              $t('WHATSAPP_INTELLIGENCE.GROUP_COUNTS', {
                active: groups.active_total || 0,
                left: groups.left_total || 0,
              })
            }}
          </p>
        </div>
        <div
          v-for="membership in groupMemberships"
          :key="`${membership.group_jid}-${membership.observed_at}`"
          class="mt-2 min-w-0"
        >
          <p class="break-words text-xs font-medium text-n-slate-12">
            {{ groupName(membership) }}
          </p>
          <p class="mt-0.5 text-[11px] text-n-slate-11">
            {{ formatGroupMembership(membership) }}
          </p>
        </div>
      </div>

      <div v-if="linkItems.length" class="rounded-lg border border-n-weak p-3">
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.LINKS') }}
        </p>
        <div
          v-for="link in linkItems"
          :key="link.url || link.title"
          class="mt-2 min-w-0"
        >
          <p class="truncate text-xs font-medium text-n-slate-12">
            {{ link.title || link.url }}
          </p>
          <p
            v-if="link.description"
            class="truncate text-[11px] text-n-slate-11"
          >
            {{ link.description }}
          </p>
        </div>
      </div>

      <div
        v-if="
          chatLabels.length ||
          chatStateItems.length ||
          profileEvents.length ||
          callItems.length
        "
        class="rounded-lg border border-n-weak p-3"
      >
        <p class="text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.ACTIVITY') }}
        </p>
        <div v-if="chatLabels.length" class="mt-2 flex flex-wrap gap-1.5">
          <span
            v-for="label in chatLabels"
            :key="label.id || labelName(label)"
            class="rounded-full bg-n-slate-3 px-2 py-1 text-[11px] text-n-slate-11 dark:bg-n-slate-4"
          >
            {{ labelName(label) }}
          </span>
        </div>
        <p
          v-for="[key, value] in chatStateItems"
          :key="key"
          class="mt-2 text-xs text-n-slate-11"
        >
          {{
            $t('WHATSAPP_INTELLIGENCE.DETAIL_ENTRY', {
              label: translateMetricLabel(key),
              value: formatChatValue(key, value),
            })
          }}
        </p>
        <p
          v-for="event in profileEvents"
          :key="event.event_id || `${event.type}-${event.occurred_at}`"
          class="mt-2 text-xs text-n-slate-11"
        >
          {{
            $t('WHATSAPP_INTELLIGENCE.DETAIL_ENTRY', {
              label: translateMetricLabel(event.type),
              value: formatDate(event.occurred_at),
            })
          }}
        </p>
        <p
          v-for="call in callItems"
          :key="
            call.id ||
            call.call_id ||
            call.event_id ||
            call.started_at ||
            call.occurred_at
          "
          class="mt-2 text-xs text-n-slate-11"
        >
          {{ formatCall(call) }}
        </p>
      </div>

      <details
        v-if="limitationItems.length"
        class="rounded-lg border border-n-weak p-3"
      >
        <summary class="cursor-pointer text-xs font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_INTELLIGENCE.DATA_QUALITY') }}
        </summary>
        <p
          v-for="[key, value] in limitationItems"
          :key="key"
          class="mt-2 text-[11px] leading-4 text-n-slate-11"
        >
          <span class="font-medium text-n-slate-12">
            {{
              $t('WHATSAPP_INTELLIGENCE.DETAIL_WITH_COLON', {
                label: translateMetricLabel(key),
                value,
              })
            }}
          </span>
        </p>
      </details>

      <div class="flex flex-wrap gap-2 border-t border-n-weak pt-3">
        <Button
          :label="$t('WHATSAPP_INTELLIGENCE.REFRESH_PROFILE')"
          :loading="isRefreshingProfile"
          :disabled="!connection"
          color="slate"
          size="sm"
          variant="faded"
          @click="refreshProfile"
        />
        <Button
          :label="$t('WHATSAPP_INTELLIGENCE.FOLLOW_PRESENCE')"
          :loading="isSubscribing"
          color="slate"
          size="sm"
          variant="faded"
          @click="subscribePresence"
        />
      </div>
    </div>
  </section>
</template>
